import { useEffect, useState } from 'react';
import { VettingOption, useGetVettingOptionsApi } from 'src/api/campaignRegistry';
import { ApplyForVettingFormData } from '../components/EditCompanyView/ApplyForVettingDialog/ApplyForVettingForm';
import { ApplyForVettingDialogProps } from '../types';

const initFormData: ApplyForVettingFormData = {
  vettingType: 'STANDARD',
  vettingPartner: 'Aegis Mobile',
  chargeForVetting: true,
};

export type ApplyForVettingDialogViewModel = {
  open: boolean;
  formData: ApplyForVettingFormData;
  submitting: boolean;
  optionsLoading: boolean;
  vettingOptions: VettingOption[];
  close: () => void;
  submit: (formData: ApplyForVettingFormData) => Promise<void>;
};

export const useApplyForVettingDialogController = (
  props: ApplyForVettingDialogProps,
): ApplyForVettingDialogViewModel => {
  const { open, onClose, onSubmit } = props;
  const getVettingOptions = useGetVettingOptionsApi();
  const [formData, setFormData] = useState<ApplyForVettingFormData>(initFormData);
  const [submitting, setSubmitting] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [vettingOptions, setVettingOptions] = useState<VettingOption[]>([]);

  useEffect(() => {
    if (open) {
      setFormData(initFormData);
      setSubmitting(false);
      setOptionsLoading(true);
      getVettingOptions()
        .then(response => {
          const options = response.data || [];
          setVettingOptions(options);
          if (options.length > 0) {
            const [firstOption] = options;
            setFormData(prev => ({
              ...prev,
              vettingType: firstOption.vettingClass as ApplyForVettingFormData['vettingType'],
              vettingPartner: firstOption.providerName,
            }));
          }
        })
        .catch(error => {
          console.error('Failed to load vetting options:', error);
          setVettingOptions([]);
        })
        .finally(() => {
          setOptionsLoading(false);
        });
    }
  }, [open, getVettingOptions]);

  const handleClose = () => {
    setFormData(initFormData);
    setSubmitting(false);
    setOptionsLoading(false);
    onClose();
  };

  const handleFormSubmit = async (data: ApplyForVettingFormData) => {
    setSubmitting(true);
    try {
      await onSubmit(data.vettingType, data.vettingPartner, data.chargeForVetting);
      handleClose();
    } catch (error) {
      console.error('Failed to submit vetting request:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    open,
    formData,
    submitting,
    optionsLoading,
    vettingOptions,
    close: handleClose,
    submit: handleFormSubmit,
  };
};
